/**
 * Modal s textem potvrzení o výplatě + možnosti sdílení.
 * Otevři: openPayoutReceipt({ musician, paidAt, items })
 *  - musician: { name }
 *  - paidAt:   ISO datum (YYYY-MM-DD)
 *  - items:    [{ event, placeName, wage, travel, payout }]
 */

function esc(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const MONTHS = ['', 'ledna', 'února', 'března', 'dubna', 'května', 'června',
                'července', 'srpna', 'září', 'října', 'listopadu', 'prosince'];

function fmtDate(d) {
    if (!d) return '';
    const dt = new Date(d);
    return `${dt.getDate()}. ${dt.getMonth() + 1}. ${dt.getFullYear()}`;
}

function fmtDateLong(d) {
    if (!d) return '';
    const dt = new Date(d);
    return `${dt.getDate()}. ${MONTHS[dt.getMonth() + 1]} ${dt.getFullYear()}`;
}

function fmtNumber(n) {
    const x = Math.round(Number(n) || 0);
    return new Intl.NumberFormat('cs-CZ').format(x);
}

/**
 * Minimalistický formát potvrzení s rozpisem mzda + doprava = celkem:
 *   [jméno muzikanta]                    [celkem]
 *
 *   [datum akce] · [místo] · [mzda] + [doprava] = [celkem]
 *   ...
 *
 *   Datum výpočtu: [datum]
 *
 * Bez labelů "mzda/doprava", bez symbolu "Kč", bez dekorací.
 * Pokud byla doprava 0, řádek obsahuje jen jednu částku.
 */
function buildReceiptText({ musician, paidAt, items }) {
    const total = items.reduce((s, i) => s + (i.payout || 0), 0);
    const name = musician.name || '';
    const totalStr = fmtNumber(total);

    const lines = [];
    lines.push(`${name}  ${totalStr}`);
    lines.push('');
    items.forEach((it) => {
        const date    = fmtDate(it.event?.event_date);
        const place   = it.placeName || it.event?.place?.name || '';
        const wage    = Math.round(it.wage   || 0);
        const travel  = Math.round(it.travel || 0);
        const payout  = Math.round(it.payout || 0);
        const amounts = travel > 0
            ? `${fmtNumber(wage)} + ${fmtNumber(travel)} = ${fmtNumber(payout)}`
            : fmtNumber(payout);
        lines.push(`${date} · ${place} · ${amounts}`);
    });
    lines.push('');
    lines.push(`Datum výpočtu: ${fmtDateLong(paidAt)}`);
    return lines.join('\n');
}

export function openPayoutReceipt({ musician, paidAt, items }) {
    // Pokud už nějaký modal je, zruš ho
    const existing = document.getElementById('payout-receipt-modal');
    if (existing) existing.remove();

    const text = buildReceiptText({ musician, paidAt, items });
    const canShare = typeof navigator.share === 'function';

    const m = document.createElement('div');
    m.id = 'payout-receipt-modal';
    m.className = 'modal-overlay open';
    m.innerHTML = `
        <div class="modal-window receipt-window">
            <div class="modal-head">
                <h2>Výpis pro ${esc(musician.name || '')}</h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <div class="modal-body">
                <textarea id="receipt-text" class="receipt-text" rows="14" spellcheck="false">${esc(text)}</textarea>
                <p class="hint">Text můžeš upravit. Tlačítka dole ho pošlou na vybranou platformu.</p>
            </div>
            <div class="modal-foot receipt-actions">
                <button class="btn" id="pr-copy" type="button">📋 Kopírovat</button>
                <button class="btn" id="pr-download" type="button">💾 Stáhnout .txt</button>
                <button class="btn" id="pr-whatsapp" type="button">💬 WhatsApp</button>
                <button class="btn" id="pr-email" type="button">✉ E-mail</button>
                ${canShare ? '<button class="btn" id="pr-share" type="button">📤 Sdílet…</button>' : ''}
                <button class="btn btn-primary" id="pr-close" type="button">Hotovo</button>
            </div>
        </div>
    `;
    document.body.appendChild(m);

    const close = () => m.remove();
    m.querySelector('.modal-close').addEventListener('click', close);
    m.querySelector('#pr-close').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });
    document.addEventListener('keydown', function onEsc(e) {
        if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); }
    });

    const getText = () => m.querySelector('#receipt-text').value;
    const flash = (btn, label) => {
        const original = btn.innerHTML;
        btn.innerHTML = label;
        btn.disabled = true;
        setTimeout(() => { btn.innerHTML = original; btn.disabled = false; }, 1600);
    };

    // 📋 Kopírovat do schránky
    m.querySelector('#pr-copy').addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(getText());
            flash(m.querySelector('#pr-copy'), '✓ Zkopírováno');
        } catch (err) {
            alert('Kopírování selhalo: ' + err.message);
        }
    });

    // 💾 Stáhnout .txt
    m.querySelector('#pr-download').addEventListener('click', () => {
        const filename = `vyplata-${(musician.name || 'muzikant').replace(/\s+/g, '-').toLowerCase()}-${paidAt || new Date().toISOString().slice(0, 10)}.txt`;
        const blob = new Blob([getText()], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    });

    // 💬 WhatsApp (přes wa.me) — otevře picker, kam poslat
    m.querySelector('#pr-whatsapp').addEventListener('click', () => {
        const url = `https://wa.me/?text=${encodeURIComponent(getText())}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    });

    // ✉ E-mail
    m.querySelector('#pr-email').addEventListener('click', () => {
        const subject = `Výplata ${musician.name || ''}`.trim();
        const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(getText())}`;
        window.location.href = url;
    });

    // 📤 Web Share API (mobilní zařízení)
    if (canShare) {
        m.querySelector('#pr-share').addEventListener('click', async () => {
            try {
                await navigator.share({
                    title: `Výplata ${musician.name || ''}`.trim(),
                    text: getText()
                });
            } catch (err) {
                if (err.name !== 'AbortError') alert('Sdílení selhalo: ' + err.message);
            }
        });
    }
}
